import { useTimeoutEffect } from "@react-hookz/web";
import React, { useState } from "react";

import { yieldToMain } from "shared/lib/utils";

import IconEmail from "scmp-app/components/newsletter/sign-up-button/icon-email.svg";
import IconTick from "scmp-app/components/newsletter/sign-up-button/icon-tick.svg";

import {
  FollowAction,
  FollowButtonDisplay,
  FollowButtonOutline,
  FollowButtonSize,
  FollowButtonVariant,
} from "./enums";
import {
  BluePlusFollowingIconContainer,
  BluePlusFollowingText,
  GetAlertContainer,
  GetAlertFollowingText,
  StyledBlueFollowIcon,
  StyledBluePlusIcon,
  StyledBlueSpinningLoadingIcon,
  StyledBlueSquareFollowIcon,
  StyledButton,
  StyledDarkBluePlusIcon,
  StyledGrayCircleBlackTickFollowingIcon,
  StyledGrayCircleFollowIcon,
  StyledGrayCircleWhiteTickFollowingIcon,
} from "./styles";

export type LoadingRelatedProps = {
  isLoadingEnabled?: boolean;
  loadingDelay?: number;
};

export type Props = LoadingRelatedProps & {
  className?: string;
  disabled?: boolean;
  display?: FollowButtonDisplay;
  isFollowing: boolean;
  isLoading?: boolean;
  onClick: (action: FollowAction) => void;
  outline?: FollowButtonOutline;
  size?: FollowButtonSize;
  variant?: FollowButtonVariant;
};

export const FollowButton: React.FunctionComponent<Props> = ({
  className,
  disabled,
  display = FollowButtonDisplay.Text,
  isFollowing,
  isLoadingEnabled = false,
  loadingDelay = 1500,
  onClick,
  outline = FollowButtonOutline.Filled,
  size = FollowButtonSize.Normal,
  variant = FollowButtonVariant.Light,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleFollowAction = () => {
    onClick(isFollowing ? FollowAction.UnFollow : FollowAction.Follow);
  };

  useTimeoutEffect(
    () => {
      setIsLoading(false);
    },
    isLoading ? loadingDelay : undefined,
  );

  const handleClick = () => {
    void (async () => {
      await yieldToMain({ priority: "user-blocking" });
      if (isLoadingEnabled) {
        setIsLoading(true);
      }

      handleFollowAction();
    })();
  };

  const renderDisplay = () => {
    if (isLoading) {
      return <StyledBlueSpinningLoadingIcon />;
    }

    const followingIcon =
      variant === FollowButtonVariant.Homepage ? (
        <StyledGrayCircleBlackTickFollowingIcon />
      ) : (
        <StyledGrayCircleWhiteTickFollowingIcon />
      );

    switch (display) {
      case FollowButtonDisplay.BlueIcon:
        return isFollowing ? followingIcon : <StyledBlueFollowIcon />;
      case FollowButtonDisplay.BluePlusIcon:
        return isFollowing ? (
          "FOLLOWING"
        ) : (
          <BluePlusFollowingIconContainer>
            <StyledBluePlusIcon />
            <BluePlusFollowingText>FOLLOW</BluePlusFollowingText>
          </BluePlusFollowingIconContainer>
        );
      case FollowButtonDisplay.BlueSquareIcon:
        return isFollowing ? followingIcon : <StyledBlueSquareFollowIcon />;
      case FollowButtonDisplay.CircleGrayIcon:
        return isFollowing ? followingIcon : <StyledGrayCircleFollowIcon />;
      case FollowButtonDisplay.DarkBluePlusIcon:
        return isFollowing ? (
          "FOLLOWING"
        ) : (
          <BluePlusFollowingIconContainer>
            <StyledDarkBluePlusIcon />
            <BluePlusFollowingText>FOLLOW</BluePlusFollowingText>
          </BluePlusFollowingIconContainer>
        );
      case FollowButtonDisplay.GetAlert:
        return isFollowing ? (
          "FOLLOWING"
        ) : (
          <GetAlertContainer>
            <StyledDarkBluePlusIcon />
            <GetAlertFollowingText>Get Alert</GetAlertFollowingText>
          </GetAlertContainer>
        );
      case FollowButtonDisplay.MyNewsSetup:
        return "COMPLETE SETUP";
      case FollowButtonDisplay.Text:
        if (variant === FollowButtonVariant.NewsletterColumnist) {
          return isFollowing ? (
            <>
              <IconTick /> SIGNED UP
            </>
          ) : (
            <>
              <IconEmail /> SIGN UP
            </>
          );
        }
        return isFollowing ? "FOLLOWING" : "+ FOLLOW";
    }
  };

  return (
    <StyledButton
      aria-label="follow"
      className={className}
      disabled={disabled}
      isFollowing={isFollowing}
      isLoading={isLoading}
      onClick={handleClick}
      outline={outline}
      size={size}
      variant={variant}
    >
      {renderDisplay()}
    </StyledButton>
  );
};

export { StyledBlueSpinningLoadingIcon } from "./styles";
